import { useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import type { ProviderSlots } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/bookable.pb';
import dayjs, { type Dayjs } from 'dayjs';
import { formatDate } from '@frontend/date';
import { i18next, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, NakedButton, SpinningLoader, styles, Text } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../../constants';
import type { CustomProviderType } from '../../../../hooks/booking-site/use-get-appointment-types-providers';
import { useFetchAndStoreProviderAvailabilities } from '../../../../hooks/booking-site/use-get-calendar-availabilities';
import { useBookingSiteStore } from '../../../../stores/use-booking-site-store';
import { BOOKING_SITE_TRACKING_IDS } from '../../../../trackingIds/booking-site-tracking-ids';
import { startOfWeek } from '../../../../utils/date-helpers';

const WEEKDAYS = [
  i18next.t('Sunday', { ns: 'schedule' }),
  i18next.t('Monday', { ns: 'schedule' }),
  i18next.t('Tuesday', { ns: 'schedule' }),
  i18next.t('Wednesday', { ns: 'schedule' }),
  i18next.t('Thursday', { ns: 'schedule' }),
  i18next.t('Friday', { ns: 'schedule' }),
  i18next.t('Saturday', { ns: 'schedule' }),
];

interface Props {
  provider: CustomProviderType;
  providerSlots?: ProviderSlots;
  nextAvailableDay?: Dayjs | null;
  isBookableSlotsLoading?: boolean;
}

export const getSlotsGroupByDay = (slots: string[]) => {
  return slots.reduce<Record<string, string[]>>((acc, slot) => {
    const day = formatDate(slot, 'YYYY-MM-DD');
    if (!acc[day]) {
      acc[day] = [];
    }
    if (acc[day].indexOf(slot) === -1) {
      acc[day].push(slot ?? '');
    }
    return acc;
  }, {});
};

const getWeekWiseSlotCount = (slotsGroupByDay: Record<string, string[]>) =>
  Object.entries(slotsGroupByDay).reduce<Record<string, number>>(
    (acc, [date, slots]) => ({ ...acc, [dayjs(date).format('dddd')]: slots.length }),
    {}
  );

const hasSlotsInView = (slotsMap: Record<string, number>) => Object.values(slotsMap).some((count) => count > 0);

export const ProviderApptCount = ({
  provider,
  providerSlots,
  nextAvailableDay,
  isBookableSlotsLoading = false,
}: Props) => {
  const { t } = useTranslation('bookingSite');
  const {
    isScheduleDefinitionsEnabled,
    isSchedularV3Enabled,
    setSelectedProvider,
    allProviderAvailabilities,
    selectedLocationTimeZone,
    selectedAppointmentLeadTime,
    setAllProviderAvailabilities,
    setWeekAnchorDate,
    setWeekInView,
  } = useBookingSiteStore([
    'isScheduleDefinitionsEnabled',
    'isSchedularV3Enabled',
    'setSelectedProvider',
    'allProviderAvailabilities',
    'selectedLocationTimeZone',
    'selectedAppointmentLeadTime',
    'setAllProviderAvailabilities',
    'setWeekAnchorDate',
    'setWeekInView',
  ]);

  const { isLoading } = useFetchAndStoreProviderAvailabilities({
    providerId: provider.id ?? '',
    providerCalendarId: provider.calendarId ?? '',
  });

  const appointmentLeadTime = useMemo(
    () =>
      isSchedularV3Enabled
        ? dayjs(selectedAppointmentLeadTime).tz(selectedLocationTimeZone)
        : dayjs(selectedAppointmentLeadTime),
    [isSchedularV3Enabled, selectedAppointmentLeadTime, selectedLocationTimeZone]
  );

  const filterByLeadTime = useCallback(
    (time: string) => {
      if (isSchedularV3Enabled) {
        return dayjs.tz(time, selectedLocationTimeZone).isAfter(appointmentLeadTime);
      }
      return dayjs(time).isAfter(appointmentLeadTime);
    },
    [appointmentLeadTime, isSchedularV3Enabled, selectedLocationTimeZone]
  );

  const weekWiseBookableSlotCount = useMemo(() => {
    const slotDateTimes =
      providerSlots?.slots
        ?.map((slot) => (slot.startDateTime ? formatDate(slot.startDateTime, 'YYYY-MM-DDTHH:mm:ss') : ''))
        .filter(Boolean)
        .filter((time) => filterByLeadTime(time)) ?? [];

    return getWeekWiseSlotCount(getSlotsGroupByDay(slotDateTimes));
  }, [providerSlots?.slots, filterByLeadTime]);

  useEffect(() => {
    if (!isScheduleDefinitionsEnabled) return;

    if (isBookableSlotsLoading) {
      setAllProviderAvailabilities(provider.id || '', []);
    } else {
      const providerAvailabilities =
        providerSlots?.slots
          ?.flatMap((slot) => (slot.startDateTime ? formatDate(slot.startDateTime, 'YYYY-MM-DDTHH:mm:ss') : ''))
          .filter(Boolean)
          .filter((time) => filterByLeadTime(time)) ?? [];
      setAllProviderAvailabilities(provider.id || '', providerAvailabilities);
    }
  }, [
    isBookableSlotsLoading,
    isScheduleDefinitionsEnabled,
    provider.id,
    providerSlots?.slots,
    setAllProviderAvailabilities,
    filterByLeadTime,
  ]);

  const weekWiseSlotCount = useMemo(() => {
    const selectedProviderAvailabilities = (provider.id ? allProviderAvailabilities[provider.id] : []) ?? [];

    const filteredAvailabilities = selectedProviderAvailabilities.filter((time) => filterByLeadTime(time));

    return getWeekWiseSlotCount(getSlotsGroupByDay(filteredAvailabilities));
  }, [allProviderAvailabilities, provider.id, filterByLeadTime]);

  const shouldShowNextAvailableSlotsButton =
    isScheduleDefinitionsEnabled && !hasSlotsInView(weekWiseBookableSlotCount) && !!nextAvailableDay;

  const handleGoToNextAvailableSlots = () => {
    if (!nextAvailableDay) {
      return;
    }

    setWeekAnchorDate(nextAvailableDay.format(DATETIME_FORMATS.US_DATE));

    const weekStartDate = startOfWeek(nextAvailableDay, selectedLocationTimeZone);
    setWeekInView(weekStartDate.toISOString());
  };

  return (
    <div
      css={containerStyle}
      onClick={(e) => {
        if (shouldShowNextAvailableSlotsButton) e.stopPropagation();
      }}
    >
      {isBookableSlotsLoading || isLoading ? (
        <div css={{ width: '100%', alignContent: 'center' }}>
          <SpinningLoader size='small' />
        </div>
      ) : shouldShowNextAvailableSlotsButton ? (
        <div css={[styles.flexCenter, { width: '100%' }]}>
          <Button
            variant='secondary'
            style={{ minWidth: 'max-content' }}
            trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.nextAvailableSlotsButton}
            onClick={handleGoToNextAvailableSlots}
          >
            {t('Go to next available slots')}
          </Button>
        </div>
      ) : (
        <WeekWiseSlots
          slotsMap={isScheduleDefinitionsEnabled ? weekWiseBookableSlotCount : weekWiseSlotCount}
          onSelect={(day) => setSelectedProvider(provider, day)}
        />
      )}
    </div>
  );
};

export const AllProvidersApptCount = () => {
  const { setSelectedProvider, allProviderAvailabilities } = useBookingSiteStore([
    'setSelectedProvider',
    'allProviderAvailabilities',
  ]);

  const weekWiseSlotsMap = useMemo(() => {
    const allSlots = Object.values(allProviderAvailabilities).flat();
    return getWeekWiseSlotCount(getSlotsGroupByDay(allSlots));
  }, [allProviderAvailabilities]);

  return (
    <div css={containerStyle}>
      <WeekWiseSlots slotsMap={weekWiseSlotsMap} onSelect={(day) => setSelectedProvider('any', day)} />
    </div>
  );
};

const WeekWiseSlots = ({
  slotsMap,
  onSelect,
}: {
  slotsMap: Record<string, number>;
  onSelect: (day: string) => void;
}) => {
  const { t } = useTranslation('bookingSite');

  return WEEKDAYS.map((day) => {
    const count = slotsMap[day] ?? 0;
    return (
      <div key={day} css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}>
        <Text css={{ margin: 0 }}>{day.substring(0, 3)}</Text>
        <NakedButton
          css={buttonStyle(!!count)}
          disabled={!count}
          trackingId={BOOKING_SITE_TRACKING_IDS.providerStep.weekDaySelect}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(day);
          }}
        >
          {count ? t('{{count}} appts', { count }) : t('No appts')}
        </NakedButton>
      </div>
    );
  });
};

const containerStyle = css({
  display: 'flex',
  position: 'relative',
  gap: theme.spacing(1),
  minHeight: 100,
  width: '100%',
  margin: theme.spacing(1.5, 1),
});

const buttonStyle = (hasCount: boolean) =>
  css({
    height: '100%',
    padding: theme.spacing(1),
    fontSize: theme.fontSize(16),
    borderRadius: theme.borderRadius.small,
    textAlign: 'left',
    border: `1px solid ${hasCount ? theme.colors.neutral30 : theme.colors.neutral20} !important`, // need to override disable button style
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: theme.colors.neutral10,
    },
  });
